<template>
  <Dialog
    v-model:visible="visibleProxy"
    header="Richiamami"
    modal
    :style="{ width: '1100px' }"
    :contentStyle="{ minHeight: '680px' }"
    :breakpoints="{ '1200px': '94vw' }"
    class="recall-dialog"
  >
    <div v-if="loading && !appointmentDates.length" class="recall-loading">
      <i class="pi pi-spin pi-spinner"></i>
      <span>Caricamento appuntamenti...</span>
    </div>

    <div v-else class="recall-layout">
      <Calendar
        v-model="selectedDate"
        inline
        dateFormat="dd/mm/yy"
        :firstDayOfWeek="1"
      >
        <template #date="{ date }">
          <span
            class="recall-day"
            :class="{ 'has-appointment': hasAppointment(date) }"
          >
            {{ date.day }}
          </span>
        </template>
      </Calendar>

      <div class="recall-details">
        <div class="recall-details-head">
          <h4 class="recall-details-title">
            {{ selectedDateLabel }}
          </h4>
          <label class="recall-mine">
            <InputSwitch v-model="onlyMine" />
            <span>Solo i miei</span>
          </label>
        </div>

        <div class="recall-list">
          <div v-if="listLoading" class="recall-list-loading">
            <i class="pi pi-spin pi-spinner"></i>
          </div>

          <div v-else-if="!recalls.length" class="recall-empty">
            {{ onlyMine ? 'Nessun richiamami assegnato a te in questo giorno.' : 'Nessun appuntamento in questo giorno.' }}
          </div>

          <div
            v-for="item in recalls"
            :key="item.id"
            class="recall-card"
            :class="{ clickable: canOpenIncall(item), unassigned: isUnassigned(item) }"
            :title="canOpenIncall(item) ? 'Apri In Call del cliente' : null"
            @click="openClientIncall(item)"
          >
            <div class="recall-card-header">
              <strong>{{ item.cliente }}</strong>
              <span class="recall-fascia">{{ item.fascia_oraria }}</span>
            </div>
            <div class="recall-row">
              <span class="label">Telefono</span>
              <span>{{ item.phone || '—' }}</span>
            </div>
            <div class="recall-row">
              <span class="label">Email</span>
              <span>{{ item.email || '—' }}</span>
            </div>
            <div class="recall-row">
              <span class="label">Motivazione</span>
              <span>{{ item.motivazione || '—' }}</span>
            </div>
            <div class="recall-row">
              <span class="label">Assegnato a</span>
              <span :class="{ unassigned: isUnassigned(item) }">
                {{ item.assegnato || 'Non ancora assegnato' }}
              </span>
            </div>
          </div>
        </div>

        <Paginator
          v-if="totalRecords > 0"
          class="recall-paginator"
          :first="(currentPage - 1) * perPage"
          :rows="perPage"
          :totalRecords="totalRecords"
          :alwaysShow="false"
          template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport"
          currentPageReportTemplate="{first} a {last} di {totalRecords}"
          @page="onPageChange"
        />
      </div>
    </div>
  </Dialog>
</template>

<script>
import { computed, getCurrentInstance, nextTick, ref, watch } from 'vue'
import PanelAdminService from '@/services/ask-admin/panel-admin'

export default {
  name: 'RecallClientCalendarDialog',
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:visible'],
  setup(props, { emit }) {
    const self = getCurrentInstance()
    const http = self.appContext.config.globalProperties.$http
    const eventBus = self.appContext.config.globalProperties.$eventBus
    const panelAdminService = new PanelAdminService(http, eventBus)

    const loading = ref(false)
    const listLoading = ref(false)
    const recalls = ref([])
    const appointmentDates = ref([])
    const selectedDate = ref(new Date())
    const currentPage = ref(1)
    const perPage = ref(8)
    const totalRecords = ref(0)
    const onlyMine = ref(true)
    let suppressReload = false

    const visibleProxy = computed({
      get: () => props.visible,
      set: (value) => emit('update:visible', value),
    })

    const formatDateKey = (date) => {
      if (!date) return ''
      const d = date instanceof Date ? date : new Date(date)
      const year = d.getFullYear()
      const month = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    }

    const selectedDateKey = computed(() => formatDateKey(selectedDate.value))

    const selectedDateLabel = computed(() => {
      if (!selectedDate.value) return 'Seleziona un giorno'
      return selectedDate.value.toLocaleDateString('it-IT', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    })

    const hasAppointment = (date) => {
      const year = date.year
      const month = String(date.month + 1).padStart(2, '0')
      const day = String(date.day).padStart(2, '0')
      return appointmentDates.value.includes(`${year}-${month}-${day}`)
    }

    const isUnassigned = (item) => {
      if (item && Object.prototype.hasOwnProperty.call(item, 'is_assigned')) {
        return !item.is_assigned
      }

      const type = String(item?.assigned_user_type || '').toLowerCase().trim()
      if (type === 'area') {
        return false
      }

      return !item?.id_assigned_user
        || type !== 'user'
        || item?.assegnato === 'Non ancora assegnato'
    }

    const canOpenIncall = (item) => Boolean(item?.id_cliente)

    const openClientIncall = (item) => {
      if (!canOpenIncall(item)) return

      const params = new URLSearchParams({
        search_type: 'cliente',
        search_by: 'id',
        q: String(item.id_cliente),
      })
      if (item.id_ticket) {
        params.set('ticket_id', String(item.id_ticket))
      }

      visibleProxy.value = false
      window.open(`/in-call?${params.toString()}`, '_blank')
    }

    const loadRecalls = async (page = currentPage.value) => {
      const isFirstLoad = !appointmentDates.value.length
      if (isFirstLoad) {
        loading.value = true
      } else {
        listLoading.value = true
      }

      try {
        const res = await panelAdminService.getRecallClientCalendar({
          date: selectedDateKey.value,
          page,
          per_page: perPage.value,
          mine: onlyMine.value ? 1 : 0,
        })

        recalls.value = Array.isArray(res?.data) ? res.data : []
        const days = Array.isArray(res?.days) ? res.days : []
        appointmentDates.value = days.map((day) => String(day.date || '').slice(0, 10)).filter(Boolean)

        const pagination = res?.pagination || {}
        currentPage.value = Number(pagination.current_page || page || 1)
        perPage.value = Number(pagination.per_page || perPage.value)
        totalRecords.value = Number(pagination.total || 0)
      } catch (error) {
        recalls.value = []
        totalRecords.value = 0
        eventBus.emit('data-load-error', { message: error })
      } finally {
        loading.value = false
        listLoading.value = false
      }
    }

    const onPageChange = (event) => {
      currentPage.value = event.page + 1
      loadRecalls(currentPage.value)
    }

    watch(
      () => props.visible,
      async (open) => {
        if (!open) return
        suppressReload = true
        const today = new Date()
        selectedDate.value = today
        currentPage.value = 1
        appointmentDates.value = []
        onlyMine.value = true
        await nextTick()
        suppressReload = false
        loadRecalls(1)
      }
    )

    watch(onlyMine, () => {
      if (!props.visible || suppressReload) return
      currentPage.value = 1
      loadRecalls(1)
    })

    watch(selectedDateKey, (next, prev) => {
      if (suppressReload || !props.visible || !prev || next === prev) return
      currentPage.value = 1
      loadRecalls(1)
    })

    return {
      visibleProxy,
      loading,
      listLoading,
      recalls,
      appointmentDates,
      selectedDate,
      selectedDateLabel,
      currentPage,
      perPage,
      totalRecords,
      onlyMine,
      hasAppointment,
      isUnassigned,
      canOpenIncall,
      openClientIncall,
      onPageChange,
    }
  },
}
</script>

<style scoped>
.recall-loading,
.recall-list-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 1.5rem 0;
  color: #4b5563;
}

.recall-layout {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 1.75rem;
  align-items: start;
  min-height: 620px;
}

.recall-layout :deep(.p-datepicker) {
  padding: 0.85rem 1rem 1rem;
  font-size: 1.05rem;
}

.recall-layout :deep(.p-datepicker .p-datepicker-header) {
  padding: 0.75rem 0.5rem 1rem;
}

.recall-layout :deep(.p-datepicker .p-datepicker-title) {
  font-size: 1.15rem;
}

.recall-layout :deep(.p-datepicker table th) {
  padding: 0.55rem;
  font-size: 0.9rem;
}

.recall-layout :deep(.p-datepicker table td) {
  padding: 0.35rem;
}

.recall-details {
  min-width: 0;
  display: flex;
  flex-direction: column;
  max-height: 640px;
}

.recall-details-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}

.recall-details-title {
  margin: 0;
  text-transform: capitalize;
  font-size: 1.2rem;
}

.recall-mine {
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  margin: 0;
  color: #0d3c61;
  font-size: 0.95rem;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
}

.recall-list {
  overflow-y: auto;
  flex: 1;
  min-height: 0;
  padding-right: 4px;
}

.recall-empty {
  color: #6b7280;
  font-size: 1.05rem;
}

.recall-card {
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 1.05rem 1.15rem;
  margin-bottom: 0.9rem;
}

.recall-card.unassigned {
  border-color: #f59e0b;
  background: #fffbeb;
}

.recall-card.clickable {
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
}

.recall-card.clickable:hover {
  background: #eef4f9;
  border-color: #0d3c61;
  box-shadow: 0 2px 8px rgba(13, 60, 97, 0.12);
}

.recall-card.unassigned.clickable:hover {
  background: #fef3c7;
  border-color: #d97706;
}

.recall-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
  font-size: 1.05rem;
}

.recall-fascia {
  background: #0d3c61;
  color: #fff;
  border-radius: 999px;
  padding: 0.28rem 0.8rem;
  font-size: 0.82rem;
  font-weight: 700;
  white-space: nowrap;
}

.recall-row {
  display: grid;
  grid-template-columns: 130px 1fr;
  gap: 0.5rem;
  margin-bottom: 0.45rem;
  font-size: 1rem;
}

.recall-row .label {
  color: #6b7280;
  font-weight: 600;
}

.unassigned {
  color: #b45309;
  font-weight: 700;
}

.recall-paginator {
  margin-top: 0.5rem;
  padding: 0;
}

.recall-day {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.6rem;
  height: 2.6rem;
  border-radius: 999px;
  font-size: 1rem;
}

.has-appointment {
  background: #0d3c61;
  color: #fff;
  font-weight: 700;
}

@media (max-width: 960px) {
  .recall-layout {
    grid-template-columns: 1fr;
    min-height: 0;
  }

  .recall-details {
    max-height: 480px;
  }
}
</style>
